<template>
    <section class="professional-materials-panel" :data-professional="professionalType" :aria-label="`${role.professionalTypeDescription}资料`">
        <header class="professional-materials-heading">
            <div>
                <p>{{ isPhotographer ? 'PHOTOGRAPHER MATERIALS' : 'MAKEUP ARTIST MATERIALS' }}</p>
                <h2>{{ role.professionalTypeDescription }}资料</h2>
                <span>{{ panelSubtitle }}</span>
            </div>
            <button type="button" aria-label="收起资料面板" @click="$emit('close')">收起</button>
        </header>

        <nav class="professional-materials-tabs" :aria-label="`${role.professionalTypeDescription}资料导航`">
            <button
                v-for="tab in tabs"
                :key="tab.key"
                type="button"
                :class="{ active: activeTab === tab.key }"
                :aria-current="activeTab === tab.key ? 'page' : null"
                @click="selectTab(tab.key)">{{ tab.label }}</button>
        </nav>

        <div v-if="materialsLoading" class="professional-materials-state">正在加载当前已通过资料…</div>
        <div v-else-if="materialsError" class="professional-materials-state professional-materials-state--error">{{ materialsError }}</div>

        <template v-else>
            <section v-if="activeTab === 'profile'" class="professional-materials-content">
                <article class="professional-material-card">
                    <header class="professional-material-card-heading"><h3>基础资料</h3><button type="button" :disabled="!canEditProfile" @click="profileEditDialogVisible = true">编辑</button></header>
                    <professional-application-attachment-list
                        title="本人真实照片"
                        :attachments="displayProfile.personalPhoto ? [displayProfile.personalPhoto] : []"
                        :theme="isMakeupArtist ? 'makeup' : 'photographer'"
                        empty-copy="暂无已审核通过的本人真实照片。"
                        @image-error="loadCurrentProfile().catch(() => {})" />
                    <p v-if="displayProfile.personalPhotoApprovedAt" class="professional-portrait-approved-at">照片通过时间：{{ displayProfile.personalPhotoApprovedAt }}</p>
                    <dl class="professional-materials-list">
                        <div><dt>联系电话</dt><dd>{{ displayProfile.phone || '暂无可展示信息' }}</dd></div>
                        <div><dt>服务地区</dt><dd>{{ displayProfile.region || '暂无可展示信息' }}</dd></div>
                        <div><dt>专业简介</dt><dd class="professional-materials-intro">{{ displayProfile.professionalIntro || '暂无可展示信息' }}</dd></div>
                    </dl>
                    <professional-application-attachment-list
                        title="已签署协议"
                        :attachments="displayProfile.signedAgreement ? [displayProfile.signedAgreement] : []"
                        :theme="isMakeupArtist ? 'makeup' : 'photographer'"
                        empty-copy="暂无可展示的协议附件。" />
                </article>

                <article v-if="isPhotographer" class="professional-material-card">
                    <header class="professional-material-card-heading"><h3>摄影设备</h3><button type="button" :disabled="!canSubmitEquipment" @click="equipmentDialogVisible = true">新增设备</button></header>
                    <p v-if="!canSubmitEquipment" class="professional-materials-notice">{{ equipmentActionNotice }}</p>
                    <p v-if="!materials.equipments.length" class="professional-materials-empty">暂无已通过的设备资料。</p>
                    <section v-for="equipment in materials.equipments" :key="`equipment-${equipment.id || equipment.brandModel}`" class="professional-material-item">
                        <header><strong>{{ [equipment.equipmentType, equipment.brandModel].filter(Boolean).join(' · ') || '摄影设备' }}</strong><button v-if="equipment.id" type="button" :disabled="!isProfessionalActive" @click="deleteCurrentMaterial('equipment', equipment.id)">删除</button></header>
                        <professional-application-attachment-list title="设备证明" :attachments="equipment.proofImages" />
                    </section>
                </article>

                <article class="professional-material-card">
                    <header class="professional-material-card-heading"><h3>专业证书</h3><button type="button" :disabled="!canSubmitCertificate" @click="certificateDialogVisible = true">新增证书</button></header>
                    <p v-if="!canSubmitCertificate" class="professional-materials-notice">{{ certificateActionNotice }}</p>
                    <p v-if="!materials.certificates.length" class="professional-materials-empty">暂无已通过的证书资料。</p>
                    <section v-for="certificate in materials.certificates" :key="`certificate-${certificate.id || certificate.certificateName}`" class="professional-material-item">
                        <header><strong>{{ certificate.certificateName || '专业证书' }}</strong><button v-if="certificate.id" type="button" :disabled="!isProfessionalActive" @click="deleteCurrentMaterial('certificate', certificate.id)">删除</button></header>
                        <professional-application-attachment-list title="证书附件" :attachments="certificate.file ? [certificate.file] : []" :theme="isMakeupArtist ? 'makeup' : 'photographer'" />
                    </section>
                </article>
            </section>

            <section v-else-if="activeTab === 'styles'" class="professional-materials-content">
                <header class="professional-style-heading">
                    <div><h3>我的风格</h3><p>按摄影主题分类展示当前审核通过且仍有效的风格。</p></div>
                    <button type="button" :disabled="!canSubmitStyle" @click="styleDialogVisible = true">新增风格</button>
                </header>
                <p v-if="!canSubmitStyle" class="professional-style-notice">{{ styleActionNotice }}</p>
                <p v-if="stylesLoading" class="professional-materials-empty">正在加载当前摄影风格…</p>
                <p v-else-if="stylesError" class="professional-materials-notice">{{ stylesError }}</p>
                <template v-else-if="!currentStyles.styles.length">
                    <p class="professional-style-notice">暂不具备风格接单资格。请新增风格并等待至少一个风格审核通过。</p>
                    <p class="professional-materials-empty">暂无已通过的摄影风格。</p>
                </template>
                <article v-for="group in styleGroups" :key="`theme-${group.themeId || group.themeName}`" class="professional-style-theme-card">
                    <header><h4>{{ group.themeName || '未分类主题' }}</h4><span>{{ group.styles.length }} 个风格</span></header>
                    <div class="professional-style-chip-list">
                        <span v-for="style in group.styles" :key="`style-${style.id || `${style.themeId}-${style.styleId}`}`" class="professional-style-chip">
                            {{ style.styleName || '未命名风格' }}
                        </span>
                    </div>
                </article>
            </section>

            <section v-else class="professional-materials-content">
                <p v-if="applicationListLoading" class="professional-materials-empty">正在加载审核提交记录…</p>
                <p v-else-if="applicationListError" class="professional-materials-state professional-materials-state--error">{{ applicationListError }} <button type="button" @click="loadApplicationList()">重试</button></p>
                <p v-else-if="!applicationList.items.length" class="professional-materials-empty">暂无审核提交记录。</p>
                <button
                    v-for="application in applicationList.items"
                    :key="application.id || `${application.applicationType}-${application.submittedAt}`"
                    type="button"
                    class="professional-application-record"
                    @click="openApplicationDetail(application)">
                    <span class="professional-record-type">{{ application.applicationTypeDescription }}</span>
                    <strong :class="applicationStatusClass(application.applicationStatus)">{{ application.applicationStatusDescription }}</strong>
                    <span v-if="application.submittedAt">提交于 {{ application.submittedAt }}</span>
                    <span v-if="application.finishedAt">完成于 {{ application.finishedAt }}</span>
                </button>
                <div v-if="applicationList.total > applicationList.pageSize" class="professional-record-pagination">
                    <button type="button" :disabled="applicationListLoading || applicationList.pageNo <= 1" @click="loadApplicationList(applicationList.pageNo - 1)">上一页</button>
                    <span>第 {{ applicationList.pageNo }} 页，共 {{ Math.ceil(applicationList.total / applicationList.pageSize) }} 页</span>
                    <button type="button" :disabled="applicationListLoading || applicationList.pageNo * applicationList.pageSize >= applicationList.total" @click="loadApplicationList(applicationList.pageNo + 1)">下一页</button>
                </div>
            </section>
        </template>

        <photographer-style-update-dialog
            v-if="isPhotographer"
            :visible="styleDialogVisible"
            :current-styles="currentStyles.styles"
            :pending-styles="currentStyles.pendingStyles"
            @close="styleDialogVisible = false"
            @submitted="handleStyleSubmitted" />

        <professional-material-update-dialog
            v-if="isPhotographer"
            :visible="equipmentDialogVisible"
            :professional-type="professionalType"
            resource-kind="equipment"
            @close="equipmentDialogVisible = false"
            @submitted="handleMaterialUpdated" />

        <professional-material-update-dialog
            :visible="certificateDialogVisible"
            :professional-type="professionalType"
            resource-kind="certificate"
            @close="certificateDialogVisible = false"
            @submitted="handleMaterialUpdated" />

        <professional-profile-edit-dialog
            :visible="profileEditDialogVisible"
            :professional-type="professionalType"
            :profile="currentProfile"
            :portrait-status="portraitSubmissionStatus"
            @close="profileEditDialogVisible = false"
            @submitted="handleProfileUpdated" />

        <professional-application-detail-dialog
            :professional-type="professionalType"
            :visible="detailDialogVisible"
            :loading="detailLoading"
            :error="detailError"
            :detail="applicationDetail"
            :show-withdraw="canWithdrawMaterialUpdate(applicationDetail)"
            :withdrawing="withdrawingApplicationId === detailApplicationId"
            @close="closeApplicationDetail"
            @withdraw="withdrawMaterialUpdate(applicationDetail)"
            @reload="reloadApplicationDetail" />
    </section>
</template>

<script>
import request from "@/tool/request";
import API from "@/tool/api";
import { regionData } from "element-china-area-data";
import {
    normalizeProfessionalApplicationDetail,
    normalizeProfessionalApplicationList,
    normalizeProfessionalCurrentMaterials,
    normalizeProfessionalProfile,
    normalizePortraitSubmissionStatus,
    normalizePhotographerCurrentStyles,
    canReloadProfessionalApplicationDetail,
    canWithdrawProfessionalMaterialUpdate,
} from "@/tool/creatorCenter.cjs";
import {
    getResultData,
    getResultMessage,
    isResultSuccess,
} from "@/tool/result";
import ProfessionalApplicationAttachmentList from "./ProfessionalApplicationAttachmentList";
import ProfessionalApplicationDetailDialog from "./ProfessionalApplicationDetailDialog";
import PhotographerStyleUpdateDialog from "./PhotographerStyleUpdateDialog";
import ProfessionalMaterialUpdateDialog from "./ProfessionalMaterialUpdateDialog";
import ProfessionalProfileEditDialog from "./ProfessionalProfileEditDialog";

const emptyPhotographerMaterials = () => ({
    equipments: [],
    certificates: [],
    pendingReview: { equipment: false, certificate: false },
});

const emptyMakeupArtistMaterials = () => ({
    certificates: [],
    pendingReview: { certificate: false },
});

const emptyProfile = () => ({
    phone: "",
    region: "",
    professionalIntro: "",
    signedAgreement: null,
    personalPhoto: null,
    personalPhotoApprovedAt: "",
});

const findRegionOption = (options, code) => (options || []).find((item) => String(item.value) === String(code)) || null;
const resolveRegionLabel = (options, code, fallback) => {
    const option = findRegionOption(options, code);
    return option ? option.label : (fallback || "");
};

export default {
    name: "ProfessionalMaterialsPanel",
    components: {
        ProfessionalApplicationAttachmentList,
        ProfessionalApplicationDetailDialog,
        PhotographerStyleUpdateDialog,
        ProfessionalMaterialUpdateDialog,
        ProfessionalProfileEditDialog,
    },
    props: {
        role: {
            type: Object,
            required: true,
        },
        professionalType: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            activeTab: "profile",
            materials: this.professionalType === "photographer" ? emptyPhotographerMaterials() : emptyMakeupArtistMaterials(),
            currentProfile: emptyProfile(),
            materialsLoading: false,
            materialsError: "",
            currentStyles: { styles: [], pendingStyles: [] },
            stylesLoading: false,
            stylesError: "",
            styleDialogVisible: false,
            equipmentDialogVisible: false,
            certificateDialogVisible: false,
            profileEditDialogVisible: false,
            portraitSubmissionStatus: { canSubmit: false, blockReason: "", message: "正在加载照片修改状态…", nextSubmitAt: "" },
            applicationList: { items: [], total: 0, pageNo: 1, pageSize: 20 },
            applicationListLoading: false,
            applicationListError: "",
            applicationDetail: null,
            detailLoading: false,
            detailError: "",
            detailDialogVisible: false,
            detailApplicationId: null,
            detailReloadAttempted: false,
            withdrawingApplicationId: null,
        };
    },
    computed: {
        isPhotographer() {
            return this.professionalType === "photographer";
        },
        isMakeupArtist() {
            return this.professionalType === "makeupArtist";
        },
        tabs() {
            const tabs = [{ key: "profile", label: "我的资料" }];
            if (this.isPhotographer) tabs.push({ key: "styles", label: "我的风格" });
            tabs.push({ key: "records", label: "审核提交记录" });
            return tabs;
        },
        isProfessionalActive() {
            return Number(this.role.currentStatus) === 1;
        },
        canEditProfile() {
            return this.isProfessionalActive || (Number(this.role.currentStatus) === 2 && this.portraitSubmissionStatus.blockReason === "PENDING_REVIEW");
        },
        canSubmitStyle() {
            return this.isProfessionalActive && !this.stylesLoading && !this.stylesError;
        },
        canSubmitEquipment() {
            return this.isProfessionalActive && !this.materials.pendingReview.equipment;
        },
        canSubmitCertificate() {
            return this.isProfessionalActive && !this.materials.pendingReview.certificate;
        },
        styleActionNotice() {
            if (this.stylesLoading) return "正在加载已生效和待审核的风格，请稍候。";
            if (this.stylesError) return "当前摄影风格暂时无法加载，请刷新后再提交。";
            return "当前摄影师身份不可用，暂不能提交风格更新。";
        },
        equipmentActionNotice() {
            if (this.materials.pendingReview.equipment) return "已有设备正在审核，审核完成后才能再次提交设备。";
            return "当前身份不可用，暂不能提交设备新增。";
        },
        certificateActionNotice() {
            if (this.materials.pendingReview.certificate) return "已有证书正在审核，审核完成后才能再次提交证书。";
            return "当前身份不可用，暂不能提交证书新增。";
        },
        panelSubtitle() {
            return this.isProfessionalActive ? "当前有效资料与审核记录" : "当前资料仅供查看";
        },
        professionalApi() {
            return API.creatorCenter[this.professionalType];
        },
        displayProfile() {
            return {
                ...this.currentProfile,
                region: this.formatProfileRegion(this.currentProfile),
            };
        },
        styleGroups() {
            return this.currentStyles.styles.reduce((groups, style) => {
                const themeId = style.themeId || "";
                const themeName = style.themeName || "未分类主题";
                const group = groups.find((item) => item.themeId === themeId);
                if (group) {
                    group.styles.push(style);
                    return groups;
                }
                groups.push({ themeId, themeName, styles: [style] });
                return groups;
            }, []);
        },
    },
    created() {
        this.loadPanelData();
    },
    methods: {
        formatProfileRegion(profile) {
            const provinces = Array.isArray(regionData) ? regionData : [];
            const province = findRegionOption(provinces, profile.provinceCode);
            const cities = province && Array.isArray(province.children) ? province.children : [];
            const city = findRegionOption(cities, profile.cityCode);
            const districts = city && Array.isArray(city.children) ? city.children : [];
            return [
                resolveRegionLabel(provinces, profile.provinceCode, profile.provinceName),
                resolveRegionLabel(cities, profile.cityCode, profile.cityName),
                resolveRegionLabel(districts, profile.districtCode, profile.districtName),
            ].filter(Boolean).join(" / ") || profile.region || "";
        },
        loadPanelData() {
            this.materialsLoading = true;
            this.materialsError = "";
            return Promise.all([this.loadCurrentMaterials(), this.loadCurrentProfile(), this.loadPortraitSubmissionStatus()])
                .catch((error) => {
                    this.materials = this.isPhotographer ? emptyPhotographerMaterials() : emptyMakeupArtistMaterials();
                    this.currentProfile = emptyProfile();
                    this.materialsError = getResultMessage(error, "当前资料暂时无法加载，请稍后重试。");
                })
                .finally(() => {
                    this.materialsLoading = false;
                });
        },
        loadCurrentMaterials() {
            return request.get(this.professionalApi.currentMaterials)
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    this.materials = normalizeProfessionalCurrentMaterials(getResultData(response), this.professionalType);
                });
        },
        loadCurrentProfile() {
            return request.get(this.professionalApi.profile)
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    this.currentProfile = normalizeProfessionalProfile(getResultData(response));
                });
        },
        loadPortraitSubmissionStatus() {
            return request.get(this.professionalApi.portraitSubmissionStatus)
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    this.portraitSubmissionStatus = normalizePortraitSubmissionStatus(getResultData(response));
                })
                .catch((error) => {
                    this.portraitSubmissionStatus = { canSubmit: false, blockReason: "", message: getResultMessage(error, "本人真实照片修改状态暂时无法加载。"), nextSubmitAt: "" };
                });
        },
        loadCurrentStyles() {
            if (!this.isPhotographer || this.stylesLoading) return Promise.resolve();
            this.stylesLoading = true;
            this.stylesError = "";
            return request.get(this.professionalApi.currentStyles)
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    this.currentStyles = normalizePhotographerCurrentStyles(getResultData(response));
                })
                .catch((error) => {
                    this.currentStyles = { styles: [], pendingStyles: [] };
                    this.stylesError = getResultMessage(error, "当前摄影风格暂时无法加载，请稍后重试。");
                })
                .finally(() => { this.stylesLoading = false; });
        },
        loadApplicationList(pageNo = 1) {
            this.applicationListLoading = true;
            this.applicationListError = "";
            return request.get(this.professionalApi.applicationList, {
                params: { pageNo, pageSize: this.applicationList.pageSize },
            })
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    this.applicationList = normalizeProfessionalApplicationList(getResultData(response));
                })
                .catch((error) => {
                    this.applicationListError = getResultMessage(error, "审核提交记录暂时无法加载，请稍后重试。");
                })
                .finally(() => { this.applicationListLoading = false; });
        },
        openApplicationDetail(application) {
            const applicationId = application && application.id;
            if (!applicationId) return;
            if (applicationId !== this.detailApplicationId) this.detailReloadAttempted = false;
            this.detailApplicationId = applicationId;
            this.detailDialogVisible = true;
            this.detailLoading = true;
            this.detailError = "";
            this.applicationDetail = null;
            return request.get(this.professionalApi.applicationDetail(applicationId))
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    this.applicationDetail = normalizeProfessionalApplicationDetail(getResultData(response), this.professionalType);
                })
                .catch((error) => {
                    this.detailError = getResultMessage(error, "申请审核详情暂时无法加载，请稍后重试。");
                })
                .finally(() => {
                    this.detailLoading = false;
                });
        },
        closeApplicationDetail() {
            this.detailDialogVisible = false;
            this.detailReloadAttempted = false;
        },
        reloadApplicationDetail() {
            if (!canReloadProfessionalApplicationDetail({
                visible: this.detailDialogVisible,
                applicationId: this.detailApplicationId,
                reloadAttempted: this.detailReloadAttempted,
            })) return Promise.resolve();
            this.detailReloadAttempted = true;
            return this.openApplicationDetail({ id: this.detailApplicationId });
        },
        canWithdrawMaterialUpdate(application) {
            return canWithdrawProfessionalMaterialUpdate(application);
        },
        withdrawMaterialUpdate(application) {
            if (!canWithdrawProfessionalMaterialUpdate(application) || !application.applicationId || this.withdrawingApplicationId) return;
            this.$confirm("撤回后，本次待审核的资料更新将不再进入审核，是否继续？", "确认撤回", {
                confirmButtonText: "确认撤回",
                cancelButtonText: "取消",
                type: "warning",
            })
                .then(() => {
                    this.withdrawingApplicationId = application.applicationId;
                    return request.post(this.professionalApi.withdrawMaterialUpdate(application.applicationId));
                })
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    this.$message.success("资料更新申请已撤回");
                    this.closeApplicationDetail();
                    return Promise.all([
                        this.loadCurrentMaterials(),
                        this.isPhotographer ? this.loadCurrentStyles() : Promise.resolve(),
                        this.loadApplicationList(this.applicationList.pageNo),
                    ]);
                })
                .then(() => this.$emit("updated"))
                .catch((error) => {
                    if (error === "cancel" || error === "close") return;
                    this.$message.error(getResultMessage(error, "撤回资料更新申请失败，请稍后重试"));
                })
                .finally(() => {
                    this.withdrawingApplicationId = null;
                });
        },
        handleStyleSubmitted() {
            this.loadCurrentStyles();
            if (this.activeTab === "records") this.loadApplicationList(this.applicationList.pageNo);
            this.$emit("updated");
        },
        handleMaterialUpdated(resourceKind) {
            this.loadCurrentMaterials().catch(() => {});
            if (this.activeTab === "records") this.loadApplicationList(this.applicationList.pageNo);
            this.$emit("updated");
        },
        handleProfileUpdated() {
            this.loadCurrentProfile().catch(() => {});
            this.loadPortraitSubmissionStatus().catch(() => {});
            this.$emit("updated");
        },
        selectTab(tab) {
            this.activeTab = tab;
            if (tab === "profile") this.loadPanelData();
            if (tab === "styles") this.loadCurrentStyles();
            if (tab === "records" && !this.applicationListLoading) this.loadApplicationList(this.applicationList.pageNo);
        },
        applicationStatusClass(status) {
            const classes = {
                1: "is-pending",
                2: "is-approved",
                3: "is-rejected",
                4: "is-withdrawn",
            };
            return classes[Number(status)] || "";
        },
        deleteCurrentMaterial(resourceKind, materialId) {
            const description = resourceKind === "equipment" ? "设备" : "证书";
            const endpoint = resourceKind === "equipment" ? this.professionalApi.deleteEquipment : this.professionalApi.deleteCertificate;
            if (!endpoint || !materialId) return;
            this.$confirm(`删除后将立即生效，确定删除这项${description}吗？`, `删除${description}`, {
                confirmButtonText: "确定删除",
                cancelButtonText: "取消",
                type: "warning",
            })
                .then(() => request.post(endpoint(materialId)))
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    this.$message.success(getResultMessage(response, `${description}已删除。`));
                    return this.loadCurrentMaterials();
                })
                .catch((error) => {
                    if (error === "cancel" || error === "close") return;
                    this.$message.error(getResultMessage(error, `${description}删除失败，请稍后重试。`));
                });
        },
    },
};
</script>

<style scoped>
.professional-materials-panel { display: grid; gap: 20px; margin-top: 28px; padding: 28px; color: #4f4857; background: #fff; border: 1px solid #dce9ef; border-radius: 24px; box-shadow: 0 16px 36px rgba(65, 101, 120, .08); }.professional-materials-panel[data-professional="makeupArtist"] { border-color: #f1dce2; box-shadow: 0 16px 36px rgba(117, 65, 89, .07); }.professional-materials-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 22px; }.professional-materials-heading p { margin: 0 0 4px; color: #5793b1; font-size: 10px; font-weight: 800; letter-spacing: .12em; }.professional-materials-panel[data-professional="makeupArtist"] .professional-materials-heading p { color: #c66a83; }.professional-materials-heading h2 { margin: 0 0 6px; color: #3f3158; font-size: 25px; letter-spacing: -.02em; }.professional-materials-heading span { color: #857f76; font-size: 12px; }.professional-materials-heading > button { min-height: 34px; padding: 7px 12px; color: #657c88; background: #fff; border: 1px solid #cfe1e9; border-radius: 8px; cursor: pointer; font: inherit; font-size: 12px; }.professional-materials-tabs { display: flex; gap: 8px; padding-bottom: 2px; border-bottom: 1px solid #edf1f3; }.professional-materials-tabs button { min-height: 36px; padding: 7px 13px; color: #7d7582; background: transparent; border: 0; border-bottom: 2px solid transparent; cursor: pointer; font: inherit; font-size: 13px; font-weight: 800; }.professional-materials-tabs button.active { color: #427d9a; border-bottom-color: #5597b5; }.professional-materials-panel[data-professional="makeupArtist"] .professional-materials-tabs button.active { color: #bc5d78; border-bottom-color: #d56886; }.professional-materials-state, .professional-materials-empty { margin: 0; color: #857f76; font-size: 12px; line-height: 1.7; }.professional-materials-state { padding: 18px 0; }.professional-materials-state--error { color: #a1495c; }.professional-materials-content { display: grid; gap: 16px; }.professional-material-card, .professional-style-card { display: grid; gap: 12px; padding: 18px; background: #f9fbfc; border: 1px solid #e5eef2; border-radius: 14px; }.professional-material-card h3, .professional-style-heading h3 { margin: 0; color: #4b6f83; font-size: 15px; }.professional-materials-panel[data-professional="makeupArtist"] .professional-material-card h3 { color: #a95570; }.professional-material-card-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }.professional-material-card-heading button { min-height: 32px; padding: 6px 10px; color: #3e7f9e; background: #fff; border: 1px solid #bcd9e6; border-radius: 8px; cursor: pointer; font: inherit; font-size: 11px; font-weight: 800; }.professional-material-card-heading button:disabled { cursor: not-allowed; opacity: .6; }.professional-materials-panel[data-professional="makeupArtist"] .professional-material-card-heading button { color: #b85d76; border-color: #edbdc9; }.professional-materials-notice { margin: -2px 0 0; padding: 8px 10px; color: #915e22; background: #fff5dc; border-radius: 8px; font-size: 11px; line-height: 1.6; }.professional-materials-list { display: grid; gap: 8px; margin: 0; }.professional-materials-list > div { display: grid; grid-template-columns: 78px minmax(0, 1fr); gap: 12px; font-size: 12px; line-height: 1.7; }.professional-materials-list dt { color: #8a8290; }.professional-materials-list dd { margin: 0; color: #514a5a; word-break: break-word; }.professional-materials-intro { white-space: pre-wrap; }.professional-material-item { display: grid; gap: 9px; padding-top: 13px; border-top: 1px solid #e5eef2; }.professional-material-item > strong { color: #5a5261; font-size: 12px; }.professional-style-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; }.professional-style-heading p { margin: 6px 0 0; color: #857f76; font-size: 12px; line-height: 1.6; }.professional-style-heading button { min-height: 38px; padding: 8px 14px; color: #fff; background: #4f90ae; border: 1px solid #4f90ae; border-radius: 9px; cursor: pointer; font: inherit; font-size: 12px; font-weight: 800; }.professional-style-heading button:disabled { cursor: not-allowed; opacity: .6; }.professional-style-notice { margin: -6px 0 0; padding: 9px 11px; color: #915e22; background: #fff5dc; border-radius: 8px; font-size: 12px; line-height: 1.6; }.professional-style-card header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }.professional-style-card header strong { color: #4b6f83; font-size: 13px; }.professional-style-card header span { padding: 3px 7px; color: #31735a; background: #e6f7ed; border-radius: 999px; font-size: 10px; font-weight: 800; }.professional-application-record { display: grid; grid-template-columns: minmax(110px, .8fr) 84px minmax(160px, 1fr) minmax(160px, 1fr); gap: 12px; align-items: center; width: 100%; padding: 16px 18px; color: #655d6a; text-align: left; background: #fff; border: 1px solid #e5ebef; border-radius: 12px; cursor: pointer; font: inherit; font-size: 12px; }.professional-application-record:hover { border-color: #a7cddd; background: #f9fcfd; }.professional-record-type { color: #4b6f83; font-weight: 800; }.professional-application-record strong { color: #5c5363; }.professional-materials-panel[data-professional="makeupArtist"] .professional-record-type { color: #b85d76; }
.professional-material-item > header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }.professional-material-item header strong { color: #5a5261; font-size: 12px; }.professional-material-item header button { padding: 3px 8px; color: #a55465; background: #fff; border: 1px solid #e5b9c3; border-radius: 6px; cursor: pointer; font: inherit; font-size: 10px; font-weight: 800; }.professional-material-item header button:disabled { cursor: not-allowed; opacity: .55; }
.professional-application-record strong.is-pending, .professional-application-record strong.is-approved, .professional-application-record strong.is-rejected, .professional-application-record strong.is-withdrawn { display: inline-flex; width: fit-content; align-items: center; justify-content: center; min-height: 26px; padding: 3px 9px; border: 1px solid; border-radius: 999px; font-size: 11px; font-weight: 800; line-height: 1.2; }.professional-application-record strong.is-pending { color: #9a4d00; background: #fff1d6; border-color: #f0bb70; }.professional-application-record strong.is-approved { color: #176b45; background: #ddf6e8; border-color: #7bc69e; }.professional-application-record strong.is-rejected { color: #b42335; background: #ffe2e6; border-color: #ee94a1; }.professional-application-record strong.is-withdrawn { color: #475467; background: #eef1f4; border-color: #b8c0ca; }
.professional-materials-panel[data-professional="makeupArtist"] .professional-materials-heading > button { color: #a95570; border-color: #edbdc9; }.professional-materials-panel[data-professional="makeupArtist"] .professional-material-card { background: #fffdfd; border-color: #f3e3e7; }.professional-materials-panel[data-professional="makeupArtist"] .professional-material-item { border-color: #f3e3e7; }.professional-materials-panel[data-professional="makeupArtist"] .professional-material-item > header strong { color: #735a69; }
.professional-style-theme-card { display: grid; gap: 14px; padding: 18px; background: #f9fbfc; border: 1px solid #dceaf0; border-radius: 14px; }.professional-style-theme-card > header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }.professional-style-theme-card h4 { margin: 0; color: #3f697e; font-size: 14px; }.professional-style-theme-card > header span { padding: 3px 8px; color: #4d7788; background: #e8f3f7; border-radius: 999px; font-size: 10px; font-weight: 800; }.professional-style-chip-list { display: flex; flex-wrap: wrap; gap: 9px; }.professional-style-chip { display: inline-flex; align-items: center; min-height: 32px; padding: 6px 11px; color: #4f6570; background: #fff; border: 1px solid #cfe2ea; border-radius: 9px; font-size: 12px; font-weight: 700; }
</style>
